/**
 * Client for the InSnip engagement backend (Cloudflare Worker + D1).
 * Privacy-first: a random anonymous id (no PII), best-effort, never blocks the UI.
 */
import type { GamePick, GameResult, GameToday } from "./types";

const BASE =
  (import.meta.env.VITE_ENGAGEMENT_URL as string | undefined) ??
  "https://insnip-engagement.insnip-app.workers.dev";

export type EngagementType =
  | "view"
  | "expand"
  | "reaction"
  | "save"
  | "dwell"
  | "share"
  | "source_click"
  // Explicit preference feedback, tracked server-side for personalisation + insight.
  | "more"
  | "less"
  | "hide"
  // A reader arriving from a push (ref=n), powers adaptive notification frequency.
  | "notif_open"
  // A reader arriving via someone's shared link, `tag` is the sharer's id (referral hook).
  | "referral"
  // Tapped a "get the app" CTA (banner / You-menu), `tag` is the surface (android/desktop/menu).
  | "app_get_click"
  // A phone browser was shown the app wall instead of the reader, `tag` is the platform. This is
  // the DENOMINATOR of the wall funnel: without it app_get_click from the wall is a numerator
  // with nothing under it, and there is no way to tell a wall nobody sees from one nobody taps.
  | "app_wall_seen"
  // The phone door (PhoneDoor.tsx) was shown, `tag` is the platform. Same role as
  // app_wall_seen, for the door that replaced the wall: the denominator without which
  // app_get_click cannot be read as a rate.
  | "app_door_seen"
  // The phone door was declined in favour of the browser, `tag` is the platform. This is the
  // number that says whether the door is a door or a wall: a decline is a reader kept, and a
  // door with no declines is one nobody could find their way past.
  | "app_door_declined"
  // The end-of-round app prompt was shown on a phone browser, `tag` is the round number. It
  // fires on every completed round, so its ratio against app_get_click is the only way to see
  // whether asking again at round 2 and round 3 earns anything or just wears the reader down.
  | "app_round_prompt"
  // A reader chose "Skip for now" on the sign-in card and went to read without an account.
  // The conversion denominator for the open first door: how many people read anyway, against
  // how many of them come back and sign in later.
  | "wall_skip"
  // A "Call it: Signal or Noise" commitment (the worker writes this event itself from
  // POST /stance; listed here so the type union stays the single source of truth).
  | "stance"
  // App-side push registration failure telemetry (native app only).
  | "push_reg_error";

const ACCOUNT_KEY = "insnip.accountUid";

/** The raw per-device anonymous id. Used as the merge source when signing in, so a
 * device's pre-sign-in seen-log + streak get folded into the account. */
export function deviceAnonId(): string {
  try {
    let id = localStorage.getItem("insnip.anonId");
    if (!id) {
      id = crypto?.randomUUID?.() ?? `a-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem("insnip.anonId", id);
    }
    return id;
  } catch {
    return "anon";
  }
}

/** The active user id for every API call: the signed-in account uid (a Firebase uid)
 * when present, else the per-device anon id. Switching identity is just setting or
 * clearing the account uid, every per-`u` server table follows automatically, which
 * is what makes the profile sync across devices (#8). */
export function anonId(): string {
  try {
    const acct = localStorage.getItem(ACCOUNT_KEY);
    if (acct) return acct;
  } catch {
    /* fall through to device id */
  }
  return deviceAnonId();
}

export function setAccountUid(uid: string): void {
  try {
    localStorage.setItem(ACCOUNT_KEY, uid);
  } catch {
    /* ignore */
  }
}

export function clearAccountUid(): void {
  try {
    localStorage.removeItem(ACCOUNT_KEY);
  } catch {
    /* ignore */
  }
}

export function accountUid(): string | null {
  try {
    return localStorage.getItem(ACCOUNT_KEY);
  } catch {
    return null;
  }
}

export function isSignedIn(): boolean {
  return Boolean(accountUid());
}

// ── THE REFERRER IN A LINK IS NEVER AN ACCOUNT UID ──────────────────────────────────
// Every per-user call is keyed on `u` alone, so a signed-in reader's Firebase uid inside a
// share link handed anyone who received it the power to act as that reader (mark stories
// seen, bind a push token, lock in their game). A pre-sign-in device id carries the same
// risk only for an anonymous history, and it is what the referral loop was built on; a
// signed-in reader's attribution rides the /shorten path, which stores the referrer server
// side and never prints it.

/** The id a link may carry as `?r=`: the device id before sign-in, nothing after. */
export function referralId(): string | null {
  return accountUid() ? null : deviceAnonId();
}

/** `url` with `r=<referralId()>` appended when there is one to append, else `url` untouched. */
export function withReferral(url: string): string {
  const r = referralId();
  if (!r) return url;
  const [base, hash = ""] = url.split("#", 2);
  const joiner = base.includes("?") ? "&" : "?";
  return `${base}${joiner}r=${encodeURIComponent(r)}${hash ? `#${hash}` : ""}`;
}

// "Call it: Signal or Noise". POST records the reader's one-tap call and returns the updated
// crowd split (the reveal must be instant); GET hydrates the active card. The POST body rides
// as text/plain so the browser skips the CORS preflight, same trick as sendEvent below.
export type StanceState = {
  cardId: string;
  signal: number;
  noise: number;
  mine: string | null;
  verdict: string | null;
};

export async function sendStance(cardId: string, stance: "signal" | "noise"): Promise<StanceState | null> {
  try {
    const r = await fetch(`${BASE}/stance`, {
      method: "POST",
      headers: { "Content-Type": "text/plain" },
      body: JSON.stringify({ u: anonId(), cardId, stance }),
    });
    if (!r.ok) return null;
    return (await r.json()) as StanceState;
  } catch {
    return null;
  }
}

export async function fetchStance(cardId: string): Promise<StanceState | null> {
  try {
    const r = await fetch(
      `${BASE}/stance?u=${encodeURIComponent(anonId())}&cardId=${encodeURIComponent(cardId)}`,
    );
    if (!r.ok) return null;
    return (await r.json()) as StanceState;
  } catch {
    return null;
  }
}

/**
 * First-party daily-edition signup (POST /subscribe on the engagement worker). Resolves
 * "ok" on success, "invalid_email" when the worker rejects the address, "error" for
 * anything else (network, rate limit, 500): the caller shows a soft message and the
 * reader keeps reading. tz_offset is minutes east of UTC, the push-token convention,
 * so the sender can hit each subscriber's own 7am.
 */
export async function subscribeEmail(
  email: string,
  source: string,
): Promise<"ok" | "invalid_email" | "error"> {
  try {
    const r = await fetch(`${BASE}/subscribe`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: email.trim(),
        u: anonId(),
        tz_offset: -new Date().getTimezoneOffset(),
        source,
      }),
    });
    if (r.ok) return "ok";
    const detail = (await r.json().catch(() => ({}))) as { error?: string };
    return detail.error === "invalid_email" ? "invalid_email" : "error";
  } catch {
    return "error";
  }
}

/**
 * Send a reader's feedback, either about one card or about the product as a whole. The endpoint
 * and the admin view behind it have been live for the app since the feedback feature shipped; the
 * website simply never had a way to reach them, so a whole surface of readers had no way to tell
 * us anything. Returns false rather than throwing: feedback failing is never worth an error page,
 * the UI just offers to try again.
 */
export async function sendFeedback(input: {
  kind: "card" | "general";
  cardId?: string;
  category: string;
  message: string;
  platform?: string;
  appVersion?: string;
}): Promise<boolean> {
  try {
    const res = await fetch(`${BASE}/feedback`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ u: anonId(), ...input }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export function sendEvent(event: {
  type: EngagementType;
  cardId?: string;
  category?: string;
  tag?: string;
  ms?: number;
}): void {
  try {
    const body = JSON.stringify({ u: anonId(), ...event });
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      // text/plain is CORS-safelisted, so the beacon sends without a preflight the browser
      // cannot issue. application/json forces that preflight and the beacon is silently
      // dropped (every view/reaction/save/share lost). The worker parses the body with
      // request.text()+JSON.parse regardless of content-type, so this is safe.
      navigator.sendBeacon(`${BASE}/event`, new Blob([body], { type: "text/plain" }));
      return;
    }
    void fetch(`${BASE}/event`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* best-effort: engagement must never break reading */
  }
}

/**
 * Mint a short share link for a card and return the full URL (e.g.
 * https://insnip.com/s/Ab3x9k), carrying this reader's id as the referrer so the share
 * loop keeps attribution. Built on the CURRENT origin so staging shares point at staging
 * and prod at prod. Returns null on any failure/timeout, so the caller falls back to the
 * plain /c/<id> permalink and sharing is never blocked or broken.
 */
export async function mintShortLink(cardId: string, timeoutMs = 1500): Promise<string | null> {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    const response = await fetch(`${BASE}/shorten`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ cardId, ref: anonId() }),
      signal: controller.signal,
    });
    clearTimeout(timer);
    if (!response.ok) return null;
    const data = await response.json();
    const code = typeof data?.code === "string" ? data.code : "";
    if (!/^[0-9a-zA-Z]{4,16}$/.test(code)) return null;
    const origin =
      typeof window !== "undefined" && /^https:\/\//.test(window.location.origin) && !/localhost/.test(window.location.origin)
        ? window.location.origin.replace(/\/+$/, "")
        : "https://insnip.com";
    return `${origin}/s/${code}`;
  } catch {
    return null;
  }
}

// ── ONE SHARE URL PER CARD, FOR EVERY SURFACE ────────────────────────────────────────
// USER, 2026-09-11: "if I share card it is short link, if I do share link then it is long
// ... keep it same in all places".
//
// They were right, and the cause was structural rather than a missed call site. The line
//     (await mintShortLink(card.id)) ?? buildCardLink(origin, card.id)
// was copy-pasted into five share functions, and ShareSheet had a sixth hand-rolled variant
// that never called mintShortLink at all. Six independent implementations of one decision
// means the share sheet and the card image can disagree about what a link to the same story
// looks like, which is exactly what shipped.
//
// So there is now one function, and every surface calls it. Three properties matter:
//
//   MEMOISED. The code for a card is minted once per session and reused. /shorten is already
//   idempotent per (cardId, referrer), so this is not about correctness, it is about the
//   second share of the same card not paying 590ms again, and about the sync accessor below
//   having something to return.
//
//   IN-FLIGHT SHARED. Opening the sheet and tapping an image share in quick succession must
//   not fire two mints; both await the same promise.
//
//   SYNC ACCESSOR. navigator.share() must be reached without an await in between or iOS
//   Safari can treat the transient activation as spent and throw NotAllowedError. Handlers
//   read the warm value synchronously; anything already awaiting (the image paths render a
//   canvas first) uses the async form.
//
// The fallback carries ?r= too for an anonymous reader, so a slow mint degrades to a link that
// is longer but not weaker: attribution is identical either way, because /shorten stores the
// referrer and the /s/<code> function reattaches it as ?r= when it redirects. A signed-in
// reader's long link carries no r= at all (see referralId), so their uid never leaves the
// device in a URL; only the short path attributes them.
const shortLinkCache = new Map<string, string>();
const shortLinkInFlight = new Map<string, Promise<string | null>>();

function shareOrigin(): string {
  return typeof window !== "undefined" &&
    /^https:\/\//.test(window.location.origin) &&
    !/localhost/.test(window.location.origin)
    ? window.location.origin.replace(/\/+$/, "")
    : "https://insnip.com";
}

/** The long permalink, used until a short code exists and whenever minting fails. */
function longCardUrl(cardId: string): string {
  return withReferral(`${shareOrigin()}/c/${encodeURIComponent(cardId)}`);
}

/**
 * THE share URL for a card. Short code when we can get one, long permalink when we cannot,
 * never null and never throws, so no share path can be blocked by this.
 */
export async function cardShareUrl(cardId: string): Promise<string> {
  const cached = shortLinkCache.get(cardId);
  if (cached) return cached;
  let pending = shortLinkInFlight.get(cardId);
  if (!pending) {
    pending = mintShortLink(cardId);
    shortLinkInFlight.set(cardId, pending);
    void pending.finally(() => shortLinkInFlight.delete(cardId));
  }
  const short = await pending;
  if (short) shortLinkCache.set(cardId, short);
  return short ?? longCardUrl(cardId);
}

/**
 * The same URL without awaiting, for handlers that must stay inside the user's tap. Returns
 * the short link once warm, the long permalink before that, so callers pair it with
 * prewarmCardShareUrl() rather than handling a null.
 */
export function cardShareUrlSync(cardId: string): string {
  return shortLinkCache.get(cardId) ?? longCardUrl(cardId);
}

/** Start minting early (on card view, on sheet open) so the sync read is already short. */
export function prewarmCardShareUrl(cardId: string): void {
  if (!cardId || shortLinkCache.has(cardId)) return;
  void cardShareUrl(cardId).catch(() => {});
}

/** Fetch this reader's server-side category affinities (empty on any failure). */
export async function fetchServerWeights(timeoutMs = 1200): Promise<Record<string, number>> {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    const response = await fetch(`${BASE}/weights?u=${encodeURIComponent(anonId())}`, {
      signal: controller.signal,
    });
    clearTimeout(timer);
    if (!response.ok) return {};
    const data = await response.json();
    return data && typeof data.weights === "object" ? data.weights : {};
  } catch {
    return {};
  }
}

export interface StreakState {
  current: number;
  longest: number;
  total: number;
  counted?: boolean;
  milestone?: number | null;
  /** Streak-freeze balance after this ping (additive: the server now returns it). */
  freezeTokens?: number;
  /** True when a freeze was just spent to bridge a missed day (additive). */
  freezeUsed?: boolean;
}

/** Reward balances for this reader: streak-freeze tokens + how many invited friends have been
 *  rewarded. Powers the invite + freeze UI. The tier fields are the referral-ladder position
 *  returned by the worker (additive; the web also derives them locally via computeReferralLadder). */
export interface PerksState {
  freezeTokens: number;
  referralsRewarded: number;
  /** Highest referral tier reached (worker-derived), null before the first. */
  currentTier?: string | null;
  /** Next referral tier to unlock plus how many more referrals it needs. Null once maxed. */
  nextTier?: { name: string; needed: number } | null;
  /** This reader's invite share link (carries their id as the referrer). */
  shareLink?: string;
}

/**
 * Fetch this reader's reward balances (GET /perks?u=<anonId>). Null-safe: returns
 * `{ freezeTokens: 0, referralsRewarded: 0 }` on any failure so the UI never breaks.
 */
export async function getPerks(timeoutMs = 1500): Promise<PerksState> {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    const response = await fetch(`${BASE}/perks?u=${encodeURIComponent(anonId())}`, {
      signal: controller.signal,
    });
    clearTimeout(timer);
    if (!response.ok) return { freezeTokens: 0, referralsRewarded: 0 };
    const data = await response.json();
    const toCount = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) ? Math.max(0, Math.floor(v)) : 0);
    const currentTier = typeof data?.currentTier === "string" ? data.currentTier : null;
    const nextTier =
      data?.nextTier && typeof data.nextTier.name === "string"
        ? { name: data.nextTier.name as string, needed: toCount(data.nextTier.needed) }
        : null;
    const shareLink = typeof data?.shareLink === "string" ? data.shareLink : undefined;
    return {
      freezeTokens: toCount(data?.freezeTokens),
      referralsRewarded: toCount(data?.referralsRewarded),
      currentTier,
      nextTier,
      shareLink,
    };
  } catch {
    return { freezeTokens: 0, referralsRewarded: 0 };
  }
}

/** The reader's local calendar day as YYYY-MM-DD (the client owns "today"). */
function localDay(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

/**
 * Record today's activity and advance the daily reading streak (idempotent per day).
 * Returns the new streak state, or null on any failure (UI just hides it).
 */
export async function pingStreak(timeoutMs = 1500): Promise<StreakState | null> {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    const response = await fetch(`${BASE}/streak`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ u: anonId(), day: localDay() }),
      signal: controller.signal,
    });
    clearTimeout(timer);
    if (!response.ok) return null;
    return (await response.json()) as StreakState;
  } catch {
    return null;
  }
}

/**
 * Fetch today's "Signal or Noise" game set (GET /game/today). Returns the set, or null when
 * there is no game today (404) or on any network/parse failure, so the caller degrades to a
 * calm "being prepared" state and the UI is never blocked.
 */
export async function gameToday(day: string, timeoutMs = 2500): Promise<GameToday | null> {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    const response = await fetch(
      `${BASE}/game/today?u=${encodeURIComponent(anonId())}&day=${encodeURIComponent(day)}`,
      { signal: controller.signal },
    );
    clearTimeout(timer);
    if (!response.ok) return null; // 404 "no game today" and any other non-200 degrade to null
    const data = (await response.json()) as GameToday;
    if (!data || !Array.isArray(data.items)) return null;
    return data;
  } catch {
    return null;
  }
}

/**
 * Submit the reader's picks and get the full scored reveal (POST /game/submit). Idempotent
 * server-side: replaying with the same (or empty) picks returns the stored reveal without
 * rescoring. Returns null on any failure, so the caller can show a retry.
 */
export async function gameSubmit(
  day: string,
  picks: GamePick[],
  timeoutMs = 8000,
): Promise<GameResult | null> {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    const response = await fetch(`${BASE}/game/submit`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ u: anonId(), day, picks }),
      signal: controller.signal,
    });
    clearTimeout(timer);
    if (!response.ok) return null;
    const data = (await response.json()) as GameResult;
    if (!data || !Array.isArray(data.items)) return null;
    return data;
  } catch {
    return null;
  }
}

/** Collective "trending now" signal across all readers (best-effort, hidden if empty). */
export async function fetchTrending(timeoutMs = 1500): Promise<string[]> {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    const response = await fetch(`${BASE}/trending`, { signal: controller.signal });
    clearTimeout(timer);
    if (!response.ok) return [];
    const data = await response.json();
    return Array.isArray(data?.categories) ? data.categories.filter((c: unknown): c is string => typeof c === "string").slice(0, 5) : [];
  } catch {
    return [];
  }
}
